import { z } from 'zod';

/**
 * Конфигурация для создания кастомного валидатора
 */
type CustomValidatorConfig<T> = {
  /** Zod схема для валидации */
  schema: z.ZodSchema<T>;
  /** Поля, которые нужно исключить из Zod валидации */
  excludeFields?: (keyof T)[];
  /** Кастомные валидаторы для отдельных полей */
  customValidators?: {
    [K in keyof T]?: (value: T[K], values: T) => string | undefined;
  };
};

/**
 * Создает кастомный валидатор, который объединяет Zod валидацию с кастомными правилами
 *
 * @template T - Тип данных формы
 * @param config - Конфигурация валидатора
 * @returns Функция валидации, совместимая с Mantine Form
 *
 * @example
 * ```typescript
 * // Схема с валидацией
 * const schema = z.object({
 *   name: z.string().min(1, 'Имя обязательно'),
 *   email: z.string().email('Неверный email'),
 *   manager: z.string().nullable().optional(),
 * });
 *
 * // Создание валидатора
 * const validateForm = createCustomValidator({
 *   schema,
 *   excludeFields: ['manager'], // Исключаем manager из Zod валидации
 *   customValidators: {
 *     manager: (value) => {
 *       if (!value) return 'Менеджер обязателен';
 *
 *       return undefined; // Нет ошибки
 *     },
 *   },
 * });
 */

/**
 * Функция валидации, которая объединяет Zod и кастомные правила
 *
 * @param values - Значения формы для валидации
 * @returns Объект с ошибками валидации
 */

export const createCustomValidator = <T extends Record<string, any>>({
  schema,
  excludeFields = [],
  customValidators = {},
}: CustomValidatorConfig<T>) => {
  return (values: T): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Zod валидация для всех полей кроме исключенных
    const zodResult = schema.safeParse(values);

    if (!zodResult.success) {
      zodResult.error.errors.forEach(error => {
        const fieldName = error.path[0] as string;

        if (!excludeFields.includes(fieldName as keyof T)) {
          errors[fieldName] = error.message;
        }
      });
    }

    // Кастомная валидация для указанных полей
    // Кастомная валидация для указанных полей
    Object.entries(customValidators).forEach(([fieldName, validator]) => {
      const value = values[fieldName as keyof T];
      const error = validator(value, values);

      if (error) {
        errors[fieldName] = error;
      }
    });

    return errors;
  };
};
